<template>
  <div ref="containerRef" class="app-parallax-image overflow-hidden">
    <div ref="imageRef" class="opacity-0">
      <!--
        Storyblok keeps video in the same asset library as images, so this field can hold
        an .mp4. Video is far cheaper than an animated GIF for the same result, but NuxtImg
        cannot render it, hence the branch. `muted` and `playsinline` are both required or
        iOS refuses to autoplay.
      -->
      <video
        v-if="isVideo"
        ref="videoRef"
        class="w-full"
        :src="videoSrc"
        :poster="posterSrc"
        :width="posterDimensions.width || undefined"
        :height="posterDimensions.height || undefined"
        :aria-label="alt"
        muted
        loop
        playsinline
        preload="metadata"
      />
      <NuxtImg
        v-else
        class="w-full"
        :src="image"
        :alt
        :width="getImageDimensionsFromStoryblokSrc(image).width"
        :height="getImageDimensionsFromStoryblokSrc(image).height"
        provider="storyblok"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  const { image, poster = '' } = defineProps<{
    image: string;
    alt: string;
    poster?: string;
  }>();

  const isVideo = computed(() => isVideoAssetUrl(image));
  // Both served through the Akamai proxy like images, to keep traffic off the Storyblok CDN quota
  const videoSrc = computed(() => mapStoryblokAssetUrlToAkamai(image));
  const posterSrc = computed(() => (poster ? mapStoryblokAssetUrlToAkamai(poster) : undefined));
  // Storyblok records dimensions in the path of image assets but not of video ones, so the
  // poster is what lets us reserve the right space up front and avoid a layout shift when
  // the video metadata arrives. Without a poster there is nothing to derive them from.
  const posterDimensions = computed(() =>
    poster ? getImageDimensionsFromStoryblokSrc(poster) : { width: '', height: '' },
  );

  // The GIF this replaced could not honour the setting at all; a <video> can.
  const reducedMotion = usePreferredReducedMotion();
  const prefersReducedMotion = computed(() => reducedMotion.value === 'reduce');
  const videoRef = useTemplateRef('videoRef');

  // Deliberately not the `autoplay` attribute: these pages are prerendered, so the attribute
  // would ship in the static HTML and the browser would start playing while parsing, before
  // the reduced-motion media query has resolved on the client. Dropping the attribute
  // afterwards does not pause a video that is already playing, so we drive playback here.
  watch(
    [videoRef, prefersReducedMotion],
    ([video, prefersReduced]) => {
      if (!video) return;

      if (prefersReduced) {
        video.pause();
      } else {
        // Rejects when the browser refuses playback; nothing to do but leave the poster up
        video.play().catch(() => {});
      }
    },
    { immediate: true },
  );

  const { $gsap } = useNuxtApp();
  const containerRef = useTemplateRef('containerRef');
  const imageRef = useTemplateRef('imageRef');

  function animate() {
    $gsap.to(imageRef.value, {
      opacity: 1,
      duration: 1,
      delay: 1,
    });

    $gsap.to(imageRef.value, {
      scale: 1.05,
      duration: 1,
      delay: 1,
    });

    $gsap
      .timeline({
        scrollTrigger: {
          trigger: containerRef.value,
          start: 'top bottom', // Element / viewport
          end: 'bottom top', // Element / viewport
          scrub: true,
        },
      })
      .fromTo(
        imageRef.value,
        {
          duration: 1,
          y: -80,
        },
        {
          y: 0,
        },
      );
  }

  onMounted(() => {
    animate();
  });
</script>
